import React from "react";
import { Navigate } from "react-router-dom";
import Loader from "../Loader";
import { useRBAC } from "../../context/RBACContext";

/**
 * PrivateRoute now uses RBAC context as single source of truth for auth & permissions.
 * Props:
 * - allowedRoles: array of roles (keeps existing behaviour)
 * - requiredPermission: single permission string
 * - requiredPermissions: array of permissions (ALL required)
 * - requiredAnyPermissions: array of permissions (ANY required)
 * - requiredRoles: array of roles (alias for allowedRoles but checked via RBAC)
 */
const PrivateRoute = ({
  children,
  allowedRoles,
  requiredPermission,
  requiredPermissions,
  requiredAnyPermissions,
  requiredRoles,
}) => {
  const {
    loading: rLoading,
    user,
    userRole,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasAnyRole,
  } = useRBAC();

  if (rLoading) return <Loader />;

  // Not authenticated
  if (!user) return <Navigate to="/login" replace />;

  // Role checks (preserve existing allowedRoles prop)
  if (allowedRoles && Array.isArray(allowedRoles) && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredRoles && Array.isArray(requiredRoles) && !hasAnyRole(requiredRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Permission checks
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredPermissions && Array.isArray(requiredPermissions) && !hasAllPermissions(requiredPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredAnyPermissions && Array.isArray(requiredAnyPermissions) && !hasAnyPermission(requiredAnyPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default PrivateRoute;
